import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Upload,
    FileSpreadsheet,
    X,
    AlertCircle,
    Check,
    MapPin,
} from "lucide-react";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent, Button, Loader } from "@/ui/atoms";
import {
    FormulaModal,
    buildFormulaExpression,
    parseFormulaExpression,
} from "@/ui/molecules/AmountHeaderFormulaModal";
import { OrganismFilteringRules } from "@/ui/organisms/OrganismFilteringRules";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { cn } from "@/utils/utils";
import { getNow } from "@/utils/dateService";
import { checkDeadlineDay, resolveDeadlineReferenceMonth } from "@/utils/deadlineDay";
import { downloadFile } from "@/utils/downloadXlsx";
import type { TAsyncState } from "@/store";
import type {
    TApiServiceProvider,
    TApiUser,
    TCreateDealRequest,
    TApiDealFileType,
    TApiGlType,
    TApiFinalFileDestination,
    TApiPropcodeMappingValidationResponse,
    TApiColumnExtractionResponse,
    TApiColumnMappingFileType,
    TApiColumnMappingFieldType,
    TCreateColumnMappingRequest,
    TApiOTBUploadResponse,
    TApiOTBPreValidationResponse,
    TApiDeal,
} from "@/apis/proda_finance";

/**
 * View parameters (injected dependencies)
 */
export type TViewDealCreateParams = {
    useCases: () => {
        onMount: (bpId?: string) => void;
        onUnmount: () => void;
        useServiceProviders: () => { data: TApiServiceProvider[] | null; loading: boolean };
        usePortfolioCompanies: () => {
            data: Array<{
                id: string;
                name: string;
                code: string;
            }> | null;
            loading: boolean;
        };
        useUsers: () => { data: TApiUser[] | null; loading: boolean };
        usePreselectedServiceProviderId: () => string | null;
        onCreateDeal: (data: TCreateDealRequest) => Promise<TApiDeal>;
        onExtractColumns: (
            file: File,
            headerRowNumber: number,
        ) => Promise<TApiColumnExtractionResponse>;
        onValidatePropcodeMappingFile: (
            file: File,
        ) => Promise<TApiPropcodeMappingValidationResponse>;
        onValidateOTB: (
            file: File,
            columnMappings: Array<{
                file_type: string;
                field_type: string;
                column_name: string;
                header_row_number: number;
            }>,
            startPeriod: string,
            filteringRules?: Array<{
                filtering_column_header: string;
                filtering_value: string[];
                include_filtered_values: boolean;
            }>,
        ) => Promise<TApiOTBPreValidationResponse>;
        onUploadOTB: (
            dealId: string,
            file: File,
            options?: { sheetName?: string; dateFormat?: string },
        ) => Promise<TApiOTBUploadResponse>;
        onGeneratePlaceholderOTB: (
            dealId: string,
            propertyCode: string,
        ) => Promise<TApiOTBUploadResponse>;
        onCancel: (bpId?: string) => void;
        /**
         * Called after deal is created and (if step 4) OTB upload succeeds; use to navigate away.
         * A copied deal passes the created deal and its business partner id.
         */
        onDealCreateSuccess?: (createdDeal?: TApiDeal, partnerId?: string) => void;
        /**
         * True when the business partner already has a deal with this name (any case,
         * active or not). Lets step 1 catch the conflict before the last step.
         */
        onCheckDealNameTaken: (
            name: string,
            partner: { serviceProviderId?: string; portfolioCompanyId?: string },
        ) => Promise<boolean>;
        /** Prefill for the Copy deal flow, loaded by `onLoadCopySource`. */
        useCopySource: () => TAsyncState<TDealCopyPrefill>;
        onLoadCopySource: (dealId: string) => Promise<void>;
    };
};

type TDealCreateUseCases = ReturnType<TViewDealCreateParams["useCases"]>;

/**
 * View props (from router/parent)
 */
export type TViewDealCreateProps = Record<string, never>;

import {
    TB_COLUMN_FIELDS,
    GL_COLUMN_FIELDS,
} from "@/modules/rev_apac/partners/columnMappings/columnFieldConfigs";
import {
    dealFileTypes,
    finalDestinations as allFinalDestinations,
    resolveHeaderRowNumberForApi,
    useDealWizardState,
    reconcileMappingsWithColumns,
    reconcileFilteringRulesWithColumns,
    startPeriodKey,
} from "@/ui/organisms/DealWizard";
import type {
    TColumnMappingData,
    TDealCopyPrefill,
    TDealCopyExtraFields,
} from "@/ui/organisms/DealWizard";
import { PropcodeMappingUploadSection } from "../../propcodeMapping/PropcodeMappingUploadSection";
import { DEFAULT_PROPCODE_KIND, PROPCODE_UPLOAD_HINT } from "../../propcodeMapping/constants";
import { emptyApacPropcodeMappingData } from "../../propcodeMapping/types";
import type { TApacPropcodeMappingData } from "../../propcodeMapping/types";

// Revantage APAC deals send their output to Yardi: Data Platform output is dormant.
const finalDestinations = allFinalDestinations.filter((d) => d.value === "yardi");

// Refactor 1.x migration status (see /Users/sevginmustafov/.cursor/plans/refactor-top-three-priorities_*.plan.md):
//   1.1 Column-field configs:        migrated (imported from columnFieldConfigs)
//   1.2 Wizard constants/types:      migrated (imported from DealWizard)
//   1.4 PropcodeMappingUploadSection: migrated (used in step 3 below)
//   1.5 useDealWizardState hook:     migrated (drives all step state)
//   1.6 Step component shells:       in place at @/ui/organisms/DealWizard/steps
//   1.7 DealWizard shell:            in place at @/ui/organisms/DealWizard/DealWizard
//   1.8 Full <DealWizard> migration: STAGED — wizard state already lives in
//       useDealWizardState; remaining work is mechanical: move each step's JSX
//       into the corresponding step component file under DealWizard/steps and
//       replace the step-by-step JSX below with <DealWizard steps={...} />.
//       This is intentionally deferred to keep behavior identical until visual
//       parity is verified with BDD tests.

/** A sample file was uploaded, or (Copy deal) the columns were carried over from another deal. */
const hasColumnSource = (mapping: TColumnMappingData): boolean =>
    Boolean(mapping.file) || mapping.origin === "copied";

/**
 * Creates the Deal creation view component
 */
export const createViewDealCreate = ({ useCases }: TViewDealCreateParams) => {
    /**
     * `?copyFrom=<dealId>` opens the wizard prefilled from that deal (Copy deal).
     * The prefill is loaded before the wizard mounts so it can seed initial state once.
     */
    const ViewDealCreate: React.FC<TViewDealCreateProps> = () => {
        const cases = useCases();
        const { useCopySource, onLoadCopySource } = cases;
        const [searchParams, setSearchParams] = useSearchParams();
        const copyFromDealId = searchParams.get("copyFrom");
        const copySource = useCopySource();

        useEffect(() => {
            if (copyFromDealId) void onLoadCopySource(copyFromDealId);
        }, [copyFromDealId, onLoadCopySource]);

        if (copyFromDealId) {
            if (copySource.status === "rejected") {
                return (
                    <PageLayout
                        title="Copy Deal"
                        description="The deal to copy could not be loaded"
                    >
                        <Card>
                            <CardContent className="pt-6 space-y-4">
                                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                    <span>{copySource.error.message}</span>
                                </div>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => cases.onCancel()}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="button" onClick={() => setSearchParams({})}>
                                        Start empty
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </PageLayout>
                );
            }
            if (copySource.data?.sourceDealId !== copyFromDealId) {
                return (
                    <PageLayout title="Copy Deal" description="Loading the deal to copy...">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex justify-center py-12">
                                    <Loader size="lg" />
                                </div>
                            </CardContent>
                        </Card>
                    </PageLayout>
                );
            }
        }

        return (
            <DealCreateWizard
                key={copyFromDealId ?? "new"}
                cases={cases}
                prefill={copyFromDealId ? copySource.data : null}
            />
        );
    };

    const DealCreateWizard: React.FC<{
        cases: TDealCreateUseCases;
        prefill: TDealCopyPrefill | null;
    }> = ({ cases, prefill }) => {
        const { bpId, pcId } = useParams<{ bpId?: string; pcId?: string }>();
        const {
            onMount,
            onUnmount,
            useServiceProviders,
            usePortfolioCompanies,
            useUsers,
            usePreselectedServiceProviderId,
            onCreateDeal,
            onExtractColumns,
            onValidatePropcodeMappingFile,
            onValidateOTB,
            onUploadOTB,
            onGeneratePlaceholderOTB,
            onCancel,
            onDealCreateSuccess,
            onCheckDealNameTaken,
        } = cases;

        const serviceProviders = useServiceProviders();
        const portfolioCompanies = usePortfolioCompanies();
        const users = useUsers();
        const preselectedServiceProviderId = usePreselectedServiceProviderId();

        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [deadlineError, setDeadlineError] = useState<string | null>(null);

        // Rows carry a kind rather than the C/A/P level of the shared wizard state, so the
        // propcode step keeps its own state.
        const [propcodeMappings, setPropcodeMappings] = useState<TApacPropcodeMappingData>(
            emptyApacPropcodeMappingData,
        );

        const wizardState = useDealWizardState(prefill ?? undefined);
        const {
            currentStep,
            setCurrentStep,
            tbColumnMapping,
            setTbColumnMapping,
            glColumnMapping,
            setGlColumnMapping,
            tbFilteringRules,
            setTbFilteringRules,
            glFilteringRules,
            setGlFilteringRules,
            tbFormulaOpen,
            setTbFormulaOpen,
            glFormulaOpen,
            setGlFormulaOpen,
            otbUpload,
            setOtbUpload,
            usePlaceholderOTB,
            setUsePlaceholderOTB,
            isDraggingOTB,
            setIsDraggingOTB,
        } = wizardState;

        // Form state
        const currentDate = getNow();
        const [formData, setFormData] = useState(() => ({
            name: "",
            code: "",
            service_provider_id: bpId || "",
            portfolio_company_id: pcId || "",
            finance_user_ids: [] as string[],
            deal_file_type: "tb_and_gl" as TApiDealFileType,
            gl_type: "" as string,
            import_available: true,
            start_period_month: currentDate.getMonth() + 1, // 1-12
            start_period_year: currentDate.getFullYear(),
            deadline: 2 as number | string,
            final_destination: "yardi" as TApiFinalFileDestination,
            // accepts_otb always true per spec (Enable OTB Upload); not shown in UI
            accepts_otb: true,
            ...prefill?.formData,
        }));
        // Copy deal only: the deal settings the wizard has no field for, carried over as-is.
        const [extraFields, setExtraFields] = useState<TDealCopyExtraFields | null>(
            () => prefill?.extraFields ?? null,
        );
        const [newImportBookName, setNewImportBookName] = useState("");
        const [isCheckingName, setIsCheckingName] = useState(false);
        // Set once the deal exists. A failed OTB step is then retried on its own:
        // creating the deal again would conflict on its name.
        const [createdDeal, setCreatedDeal] = useState<TApiDeal | null>(null);

        // Update service_provider_id when preselected value changes (only when not on PC route)
        useEffect(() => {
            if (!pcId && preselectedServiceProviderId && !formData.service_provider_id) {
                setFormData((prev) => ({
                    ...prev,
                    service_provider_id: preselectedServiceProviderId,
                }));
            }
        }, [pcId, preselectedServiceProviderId, formData.service_provider_id]);

        // Sync URL params (bpId, pcId) into form – exactly one: from SP page or PC page
        useEffect(() => {
            if (bpId) {
                setFormData((prev) => ({
                    ...prev,
                    service_provider_id: bpId,
                    portfolio_company_id: "",
                }));
            }
        }, [bpId]);
        useEffect(() => {
            if (pcId) {
                setFormData((prev) => ({
                    ...prev,
                    service_provider_id: "",
                    portfolio_company_id: pcId,
                }));
            }
        }, [pcId]);

        useEffect(() => {
            onMount(bpId);
            return onUnmount;
        }, [bpId, onMount, onUnmount]);

        const deadlineVal = Number(formData.deadline);
        const isDeadlineValid = formData.deadline !== "" && deadlineVal >= 1 && deadlineVal <= 28;
        // Advisory only: the deadline recurs monthly, so we check it against the chosen
        // start period (or next month once that is in the past). Never blocks Next.
        const deadlineWarning = useMemo(
            () =>
                isDeadlineValid
                    ? checkDeadlineDay(
                          deadlineVal,
                          resolveDeadlineReferenceMonth(
                              `${formData.start_period_year}-${String(formData.start_period_month).padStart(2, "0")}-01`,
                              getNow(),
                          ),
                      )
                    : null,
            [isDeadlineValid, deadlineVal, formData.start_period_year, formData.start_period_month],
        );
        const hasSp = Boolean(formData.service_provider_id?.trim());
        const hasPc = Boolean(formData.portfolio_company_id?.trim());
        const canProceedFromStep1 =
            Boolean(formData.name.trim()) &&
            ((hasSp && !hasPc) || (!hasSp && hasPc)) &&
            formData.finance_user_ids.length > 0 &&
            isDeadlineValid &&
            (formData.deal_file_type !== "tb_and_gl" || Boolean(formData.gl_type));

        // Check if column mappings are valid
        const getRequiredColumnFields = useCallback(
            (fileType: TApiColumnMappingFileType) => {
                const fields = fileType === "gl" ? GL_COLUMN_FIELDS : TB_COLUMN_FIELDS;
                return fields.filter((field) => {
                    if (field.fieldType === "period" && fileType === "gl") {
                        // Period is only required for GL files when GL Type is YTD
                        return formData.gl_type === "ytd";
                    }
                    return field.required;
                });
            },
            [formData.gl_type],
        );

        const isColumnMappingComplete = useCallback(
            (mappingData: TColumnMappingData, fileType: TApiColumnMappingFileType) => {
                if (
                    !hasColumnSource(mappingData) ||
                    mappingData.columns.length === 0 ||
                    mappingData.error
                ) {
                    return false;
                }
                const requiredFields = getRequiredColumnFields(fileType);
                return requiredFields.every((field) =>
                    mappingData.mappings[field.fieldType]?.trim(),
                );
            },
            [getRequiredColumnFields],
        );

        // Can proceed from column mapping to propcode mapping. Filtering rules are
        // included: the backend requires at least one value per rule, so a rule left
        // empty here would 422 the whole deal creation on the final step.
        const isTbAndGl = formData.deal_file_type === "tb_and_gl";
        const filteringRulesComplete =
            tbFilteringRules.every((r) => r.filteringValue.length > 0) &&
            (!isTbAndGl || glFilteringRules.every((r) => r.filteringValue.length > 0));
        const canProceedFromColumnMapping =
            isColumnMappingComplete(tbColumnMapping, "tb") &&
            (!isTbAndGl || isColumnMappingComplete(glColumnMapping, "gl")) &&
            filteringRulesComplete;

        // Latest copied-ness and filtering rules, read when a new sample's columns arrive.
        const columnOriginRef = useRef({ tb: tbColumnMapping.origin, gl: glColumnMapping.origin });
        columnOriginRef.current = { tb: tbColumnMapping.origin, gl: glColumnMapping.origin };
        const filteringRulesRef = useRef({ tb: tbFilteringRules, gl: glFilteringRules });
        filteringRulesRef.current = { tb: tbFilteringRules, gl: glFilteringRules };

        // Copy deal: a new sample that cannot be read leaves the copied columns in place.
        const keepCopiedColumns = useCallback(
            (
                prev: TColumnMappingData,
                fileType: TApiColumnMappingFileType,
                message: string,
            ): TColumnMappingData => ({
                ...prev,
                file: null,
                headerRowNumber:
                    (fileType === "gl"
                        ? prefill?.glColumnMapping.headerRowNumber
                        : prefill?.tbColumnMapping.headerRowNumber) ?? prev.headerRowNumber,
                error: null,
                warnings: [
                    `The new file could not be read (${message}). The copied columns are kept.`,
                ],
                isLoading: false,
            }),
            [prefill],
        );

        // Handle column file selection - extracts column headers from an Excel or CSV sample
        const handleColumnFileSelect = useCallback(
            async (fileType: TApiColumnMappingFileType, file: File, headerRowNumber: number) => {
                const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;

                // Validate file extension
                if (!/\.(xlsx|csv)$/i.test(file.name)) {
                    setMapping((prev) =>
                        prev.origin === "copied"
                            ? keepCopiedColumns(
                                  prev,
                                  fileType,
                                  "Only XLSX or CSV files are supported",
                              )
                            : {
                                  file: null,
                                  headerRowNumber: 1,
                                  columns: [],
                                  mappings: {} as Record<TApiColumnMappingFieldType, string>,
                                  error: "Only XLSX or CSV files are supported",
                                  warnings: [],
                                  isLoading: false,
                              },
                    );
                    return;
                }

                // Set loading state
                setMapping((prev) => ({
                    ...prev,
                    file,
                    headerRowNumber,
                    error: null,
                    isLoading: true,
                }));

                try {
                    // Send to backend for column extraction
                    const result = await onExtractColumns(file, headerRowNumber);

                    if (!result.success) {
                        const message = result.errors.join("; ") || "Column extraction failed";
                        setMapping((prev) =>
                            prev.origin === "copied"
                                ? keepCopiedColumns(prev, fileType, message)
                                : {
                                      ...prev,
                                      file: null,
                                      columns: [],
                                      mappings: {} as Record<TApiColumnMappingFieldType, string>,
                                      error: message,
                                      warnings: result.warnings,
                                      isLoading: false,
                                  },
                        );
                    } else {
                        // A new sample replacing copied columns also drops the copied
                        // filtering rules on columns it no longer has.
                        const ruleWarnings: string[] = [];
                        if (columnOriginRef.current[fileType] === "copied") {
                            const { rules, droppedColumns } = reconcileFilteringRulesWithColumns(
                                filteringRulesRef.current[fileType],
                                result.columns,
                            );
                            if (droppedColumns.length > 0) {
                                (fileType === "gl" ? setGlFilteringRules : setTbFilteringRules)(
                                    rules,
                                );
                                ruleWarnings.push(
                                    ...droppedColumns.map(
                                        (col) =>
                                            `Copied filtering rule on column "${col}" was removed: the new file has no such column.`,
                                    ),
                                );
                            }
                        }
                        setMapping((prev) => {
                            if (prev.origin !== "copied") {
                                return {
                                    ...prev,
                                    file,
                                    headerRowNumber,
                                    columns: result.columns,
                                    mappings: prev.mappings, // Preserve existing mappings
                                    error: null,
                                    warnings: result.warnings,
                                    isLoading: false,
                                };
                            }
                            // A new sample replaces copied columns: keep each copied
                            // mapping whose column still exists and flag the rest.
                            const { mappings, droppedColumns } = reconcileMappingsWithColumns(
                                prev.mappings,
                                result.columns,
                            );
                            return {
                                ...prev,
                                file,
                                headerRowNumber,
                                columns: result.columns,
                                mappings,
                                error: null,
                                warnings: [
                                    ...droppedColumns.map(
                                        (col) =>
                                            `Copied mapping to column "${col}" was cleared: the new file has no such column.`,
                                    ),
                                    ...ruleWarnings,
                                    ...result.warnings,
                                ],
                                isLoading: false,
                                origin: undefined,
                            };
                        });
                    }
                } catch (err) {
                    const message =
                        err instanceof Error ? err.message : "Failed to extract columns";
                    setMapping((prev) =>
                        prev.origin === "copied"
                            ? keepCopiedColumns(prev, fileType, message)
                            : {
                                  ...prev,
                                  file: null,
                                  columns: [],
                                  mappings: {} as Record<TApiColumnMappingFieldType, string>,
                                  error: message,
                                  warnings: [],
                                  isLoading: false,
                              },
                    );
                }
            },
            [onExtractColumns, keepCopiedColumns, setGlFilteringRules, setTbFilteringRules],
        );

        // Handle column mapping change
        const handleColumnMappingChange = useCallback(
            (
                fileType: TApiColumnMappingFileType,
                fieldType: TApiColumnMappingFieldType,
                columnName: string,
            ) => {
                const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
                setMapping((prev) => ({
                    ...prev,
                    mappings: {
                        ...prev.mappings,
                        [fieldType]: columnName,
                    },
                }));
            },
            [],
        );

        // Clear column mapping file
        const handleClearColumnFile = useCallback((fileType: TApiColumnMappingFileType) => {
            const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
            setMapping({
                file: null,
                headerRowNumber: 1,
                columns: [],
                mappings: {} as Record<TApiColumnMappingFieldType, string>,
                error: null,
                warnings: [],
                isLoading: false,
            });
        }, []);

        // Handle propcode file selection and validation - sends to backend
        const handlePropcodeFileSelect = useCallback(
            async (file: File) => {
                // Validate file extension
                if (!file.name.toLowerCase().endsWith(".xlsx")) {
                    setPropcodeMappings({
                        file: null,
                        rows: [],
                        error: "Only XLSX files are supported",
                        warnings: [],
                        isLoading: false,
                    });
                    return;
                }

                // Set loading state
                setPropcodeMappings({
                    file,
                    rows: [],
                    error: null,
                    warnings: [],
                    isLoading: true,
                });

                try {
                    // Send to backend for validation
                    const result = await onValidatePropcodeMappingFile(file);

                    if (!result.success) {
                        setPropcodeMappings({
                            file: null,
                            rows: [],
                            error: result.errors.join("; ") || "Validation failed",
                            warnings: result.warnings,
                            isLoading: false,
                        });
                    } else {
                        setPropcodeMappings({
                            file,
                            rows: result.mappings.map((m) => ({
                                kind: m.kind ?? "",
                                source_propcode: m.source_propcode,
                                target_propcode: m.target_propcode,
                            })),
                            error: null,
                            warnings: result.warnings,
                            isLoading: false,
                        });
                    }
                } catch (err) {
                    setPropcodeMappings({
                        file: null,
                        rows: [],
                        error: err instanceof Error ? err.message : "Failed to validate file",
                        warnings: [],
                        isLoading: false,
                    });
                }
            },
            [onValidatePropcodeMappingFile],
        );

        // Clear propcode file
        const handleClearPropcodeFile = useCallback(() => {
            setPropcodeMappings({
                file: null,
                rows: [],
                error: null,
                warnings: [],
                isLoading: false,
            });
        }, []);

        // What the last OTB validation was run against. Reaching step 4 again after
        // changing any of it re-validates the attached OTB, so a stale "valid" cannot
        // reach the upload, which would then fail after the deal is already created.
        const otbValidationKey = JSON.stringify([
            hasColumnSource(tbColumnMapping) ? tbColumnMapping.mappings : null,
            tbColumnMapping.headerRowNumber,
            tbFilteringRules,
        ]);
        const otbValidatedKeyRef = useRef<string | null>(null);

        // OTB file selection handler - validates immediately on upload
        const handleOTBFileSelect = useCallback(
            async (file: File) => {
                // Validate file extension
                if (!file.name.toLowerCase().endsWith(".xlsx")) {
                    setOtbUpload({
                        file: null,
                        status: "error",
                        error: "Only XLSX files are supported",
                        warnings: [],
                        uploadedFileId: null,
                        totalLineItems: 0,
                        totalAmount: "0",
                        otbPeriod: null,
                    });
                    return;
                }

                otbValidatedKeyRef.current = otbValidationKey;

                // Show loading state while validating
                setOtbUpload({
                    file,
                    status: "uploading",
                    error: null,
                    warnings: [],
                    uploadedFileId: null,
                    totalLineItems: 0,
                    totalAmount: "0",
                    otbPeriod: null,
                });

                // Build column mappings from TB mapping state
                const columnMappings: Array<{
                    file_type: string;
                    field_type: string;
                    column_name: string;
                    header_row_number: number;
                }> = [];

                if (hasColumnSource(tbColumnMapping) && tbColumnMapping.columns.length > 0) {
                    for (const [fieldType, columnName] of Object.entries(
                        tbColumnMapping.mappings,
                    )) {
                        if (columnName?.trim()) {
                            columnMappings.push({
                                file_type: "tb",
                                field_type: fieldType,
                                column_name: columnName,
                                header_row_number: resolveHeaderRowNumberForApi(
                                    tbColumnMapping.headerRowNumber,
                                ),
                            });
                        }
                    }
                }

                if (columnMappings.length === 0) {
                    setOtbUpload({
                        file: null,
                        status: "error",
                        error: "TB column mappings are required before uploading an OTB file. Please configure column mappings in Step 2.",
                        warnings: [],
                        uploadedFileId: null,
                        totalLineItems: 0,
                        totalAmount: "0",
                        otbPeriod: null,
                    });
                    return;
                }

                const startPeriod = `${formData.start_period_year}-${String(formData.start_period_month).padStart(2, "0")}-01`;

                // Apply the same TB row-filtering rules configured in Step 2 so the
                // preview (row count / balance) matches what the committed OTB keeps.
                const filteringRules = tbFilteringRules
                    .filter((r) => r.filteringColumnHeader && r.filteringValue.length > 0)
                    .map((r) => ({
                        filtering_column_header: r.filteringColumnHeader,
                        filtering_value: r.filteringValue,
                        include_filtered_values: r.includeFilteredValues,
                    }));

                try {
                    const result = await onValidateOTB(
                        file,
                        columnMappings,
                        startPeriod,
                        filteringRules,
                    );

                    if (!result.success) {
                        setOtbUpload({
                            file,
                            status: "error",
                            error: result.errors.join("; ") || "OTB validation failed",
                            warnings: result.warnings,
                            uploadedFileId: null,
                            totalLineItems: result.total_line_items,
                            totalAmount: result.total_amount,
                            otbPeriod: null,
                        });
                    } else {
                        setOtbUpload({
                            file,
                            status: "success",
                            error: null,
                            warnings: result.warnings,
                            uploadedFileId: null,
                            totalLineItems: result.total_line_items,
                            totalAmount: result.total_amount,
                            otbPeriod: null,
                        });
                    }
                } catch (err) {
                    setOtbUpload({
                        file: null,
                        status: "error",
                        error: err instanceof Error ? err.message : "Failed to validate OTB file",
                        warnings: [],
                        uploadedFileId: null,
                        totalLineItems: 0,
                        totalAmount: "0",
                        otbPeriod: null,
                    });
                }
            },
            [
                onValidateOTB,
                tbColumnMapping,
                tbFilteringRules,
                formData.start_period_year,
                formData.start_period_month,
                otbValidationKey,
            ],
        );

        // Clear OTB file
        const handleClearOTBFile = useCallback(() => {
            setOtbUpload({
                file: null,
                status: "idle",
                error: null,
                warnings: [],
                uploadedFileId: null,
                totalLineItems: 0,
                totalAmount: "0",
                otbPeriod: null,
            });
        }, []);

        // Copy deal: the copied propcode file is validated like an upload, so its targets
        // are checked against current master data. Each sheet names its kind, so the rows
        // it yields are the source's own.
        const applyCopiedPropcodes = useCallback(
            async (file: File) => {
                setPropcodeMappings({
                    file,
                    rows: [],
                    error: null,
                    warnings: [],
                    isLoading: true,
                    origin: "copied",
                });
                try {
                    const result = await onValidatePropcodeMappingFile(file);
                    setPropcodeMappings({
                        file,
                        rows: result.success
                            ? result.mappings.map((m) => ({
                                  kind: m.kind ?? "",
                                  source_propcode: m.source_propcode,
                                  target_propcode: m.target_propcode,
                              }))
                            : [],
                        error: result.success
                            ? null
                            : result.errors.join("; ") || "Validation failed",
                        warnings: result.warnings,
                        isLoading: false,
                        origin: "copied",
                    });
                } catch (err) {
                    setPropcodeMappings({
                        file,
                        rows: [],
                        error: err instanceof Error ? err.message : "Failed to validate file",
                        warnings: [],
                        isLoading: false,
                        origin: "copied",
                    });
                }
            },
            [onValidatePropcodeMappingFile],
        );

        // Copy deal: run the copied propcode mappings through the same validation as
        // an upload, so rows are checked against current master data.
        const copiedFilesAppliedRef = useRef(false);
        useEffect(() => {
            if (!prefill || copiedFilesAppliedRef.current) return;
            copiedFilesAppliedRef.current = true;
            if (prefill.propcodeFile) {
                void applyCopiedPropcodes(prefill.propcodeFile);
            }
        }, [prefill, applyCopiedPropcodes]);

        // Copy deal: the source OTB holds the opening balances for the source's own start,
        // and OTB validation does not check the period at all. So it is attached on its
        // own only when this deal starts in the same month; otherwise the user must opt in.
        const currentStartPeriod = startPeriodKey(
            formData.start_period_month,
            formData.start_period_year,
        );
        const copiedOtbPeriodMatches =
            prefill?.sourceStartPeriod != null && prefill.sourceStartPeriod === currentStartPeriod;
        const isCopiedOtbAttached =
            Boolean(prefill?.otbFile) && otbUpload.file === prefill?.otbFile;
        const copiedOtbConfirmedPeriodRef = useRef<string | null>(null);
        const copiedOtbAppliedRef = useRef(false);

        const handleUseCopiedOtb = useCallback(() => {
            if (!prefill?.otbFile) return;
            copiedOtbConfirmedPeriodRef.current = currentStartPeriod;
            setUsePlaceholderOTB(false);
            void handleOTBFileSelect(prefill.otbFile);
        }, [prefill, currentStartPeriod, setUsePlaceholderOTB, handleOTBFileSelect]);

        useEffect(() => {
            if (currentStep !== 4 || createdDeal) return;
            if (prefill?.otbFile) {
                // The start period changed after the copied OTB was attached.
                if (
                    isCopiedOtbAttached &&
                    !copiedOtbPeriodMatches &&
                    copiedOtbConfirmedPeriodRef.current !== currentStartPeriod
                ) {
                    handleClearOTBFile();
                    copiedOtbAppliedRef.current = false;
                    return;
                }
                if (!copiedOtbAppliedRef.current && copiedOtbPeriodMatches) {
                    copiedOtbAppliedRef.current = true;
                    if (!otbUpload.file && !usePlaceholderOTB) {
                        otbValidatedKeyRef.current = otbValidationKey;
                        void handleOTBFileSelect(prefill.otbFile);
                        return;
                    }
                }
            }
            if (
                otbUpload.file &&
                !usePlaceholderOTB &&
                otbUpload.status !== "uploading" &&
                otbValidatedKeyRef.current !== null &&
                otbValidatedKeyRef.current !== otbValidationKey
            ) {
                otbValidatedKeyRef.current = otbValidationKey;
                void handleOTBFileSelect(otbUpload.file);
            }
        }, [
            currentStep,
            createdDeal,
            prefill,
            isCopiedOtbAttached,
            copiedOtbPeriodMatches,
            currentStartPeriod,
            otbUpload.file,
            otbUpload.status,
            usePlaceholderOTB,
            otbValidationKey,
            handleOTBFileSelect,
            handleClearOTBFile,
        ]);

        // Calculate OTB period from start period (start_period - 1 month)
        const calculateOTBPeriod = useCallback(() => {
            const month = formData.start_period_month;
            const year = formData.start_period_year;
            if (month === 1) {
                return `${year - 1}-12-01`;
            }
            return `${year}-${String(month - 1).padStart(2, "0")}-01`;
        }, [formData.start_period_month, formData.start_period_year]);

        // Step 1 -> Step 2 (Initial Info -> Column Mapping)
        const handleNextFromStep1 = async () => {
            setError(null);

            // Validate required fields
            if (!formData.name.trim()) {
                setError("Name is required");
                return;
            }
            // Deal names are unique per business partner, and a copy stays on the same one.
            if (
                prefill &&
                formData.name.trim().toLowerCase() === prefill.sourceDealName.trim().toLowerCase()
            ) {
                setError("The copy needs a different name than the deal it was copied from.");
                return;
            }
            // Exactly one of Service Provider or Portfolio Company is required (XOR)
            const hasSp = Boolean(formData.service_provider_id?.trim());
            const hasPc = Boolean(formData.portfolio_company_id?.trim());
            if (!hasSp && !hasPc) {
                setError("Deal must belong to either a Service Provider or a Portfolio Company.");
                return;
            }
            if (hasSp && hasPc) {
                setError("Deal cannot belong to both a Service Provider and a Portfolio Company.");
                return;
            }

            if (formData.finance_user_ids.length === 0) {
                setError("At least one Finance user is required");
                return;
            }

            const deadlineVal = Number(formData.deadline);
            if (!deadlineVal || deadlineVal < 1 || deadlineVal > 28) {
                setDeadlineError("Must be between 1 and 28");
                return;
            }

            // Names are unique per business partner (any case, inactive deals included).
            // A failed lookup does not block: the create request still enforces it.
            setIsCheckingName(true);
            try {
                const taken = await onCheckDealNameTaken(
                    formData.name,
                    hasSp
                        ? { serviceProviderId: formData.service_provider_id }
                        : { portfolioCompanyId: formData.portfolio_company_id },
                );
                if (taken) {
                    setError(
                        `A deal named "${formData.name.trim()}" already exists for this business partner.`,
                    );
                    return;
                }
            } catch {
                // fall through to the backend check on create
            } finally {
                setIsCheckingName(false);
            }

            setCurrentStep(2);
        };

        // Step 2 -> Step 3 (Column Mapping -> Propcode Mapping)
        const handleNextFromStep2 = () => {
            setError(null);
            setCurrentStep(3);
        };

        // Go back from Step 2 to Step 1
        const handleBackFromStep2 = () => {
            setCurrentStep(1);
            setError(null);
        };

        // Go back from Step 3 to Step 2
        const handleBackFromStep3 = () => {
            setCurrentStep(2);
            setError(null);
        };

        // Step 3 -> Step 4 (always go to OTB upload step)
        const handleNextFromStep3 = () => {
            setError(null);

            // Validate propcode mapping file is uploaded (required)
            if (!propcodeMappings.file || propcodeMappings.rows.length === 0) {
                setError("Propcode Mapping file is required. Please upload a valid .xlsx file.");
                return;
            }

            setCurrentStep(4);
        };

        // Step 4 -> Submit (Create Deal with required OTB upload)
        const handleCreateDeal = async () => {
            setError(null);

            // Validate OTB file is uploaded or placeholder mode is active
            if (!otbUpload.file && !usePlaceholderOTB) {
                setError(
                    'OTB file is required. Please upload an Opening Trial Balance file or select "I don\'t have an OTB".',
                );
                return;
            }

            // Block deal creation if OTB validation failed
            if (!usePlaceholderOTB && otbUpload.file && otbUpload.status !== "success") {
                setError(
                    "OTB file has validation errors. Please fix the issues or upload a different file.",
                );
                return;
            }

            // Validate propcode mappings exist when using placeholder
            if (usePlaceholderOTB && propcodeMappings.rows.length === 0) {
                setError(
                    "At least one propcode mapping is required to generate a placeholder OTB.",
                );
                return;
            }

            const deadlineNum = Number(formData.deadline);
            if (!deadlineNum || deadlineNum < 1 || deadlineNum > 28) {
                setDeadlineError("Must be between 1 and 28");
                return;
            }

            setIsSubmitting(true);

            // Build start_period date (first day of the month)
            const startPeriod = `${formData.start_period_year}-${String(formData.start_period_month).padStart(2, "0")}-01`;

            try {
                // Build column mappings from the form
                const columnMappingsToCreate: TCreateColumnMappingRequest[] = [];

                // Add TB column mappings
                if (hasColumnSource(tbColumnMapping) && tbColumnMapping.columns.length > 0) {
                    for (const [fieldType, columnName] of Object.entries(
                        tbColumnMapping.mappings,
                    )) {
                        if (columnName?.trim()) {
                            columnMappingsToCreate.push({
                                file_type: "tb" as TApiColumnMappingFileType,
                                field_type: fieldType as TApiColumnMappingFieldType,
                                column_name: columnName,
                                header_row_number: resolveHeaderRowNumberForApi(
                                    tbColumnMapping.headerRowNumber,
                                ),
                                available_columns: tbColumnMapping.columns,
                            });
                        }
                    }
                }

                // Add GL column mappings if TB and GL
                if (
                    isTbAndGl &&
                    hasColumnSource(glColumnMapping) &&
                    glColumnMapping.columns.length > 0
                ) {
                    for (const [fieldType, columnName] of Object.entries(
                        glColumnMapping.mappings,
                    )) {
                        if (columnName?.trim()) {
                            columnMappingsToCreate.push({
                                file_type: "gl" as TApiColumnMappingFileType,
                                field_type: fieldType as TApiColumnMappingFieldType,
                                column_name: columnName,
                                header_row_number: resolveHeaderRowNumberForApi(
                                    glColumnMapping.headerRowNumber,
                                ),
                                available_columns: glColumnMapping.columns,
                            });
                        }
                    }
                }

                // Build propcode mappings from uploaded file
                const propcodeMappingsToCreate: Array<{
                    kind: string;
                    source_propcode: string;
                    target_propcode: string;
                }> = [];

                if (propcodeMappings.file && propcodeMappings.rows.length > 0) {
                    for (const row of propcodeMappings.rows) {
                        propcodeMappingsToCreate.push({
                            kind: row.kind,
                            source_propcode: row.source_propcode,
                            target_propcode: row.target_propcode,
                        });
                    }
                }

                // Send only SP or only PC based on route (XOR) – never both.
                // An earlier attempt may already have created the deal; then only the OTB
                // step below is retried.
                const deal =
                    createdDeal ??
                    (await onCreateDeal({
                        name: formData.name,
                        code: formData.code ? formData.code.toUpperCase() : undefined,
                        service_provider_id: pcId
                            ? undefined
                            : formData.service_provider_id || undefined,
                        portfolio_company_id: bpId
                            ? undefined
                            : formData.portfolio_company_id || undefined,
                        finance_user_ids: formData.finance_user_ids,
                        deal_file_type: formData.deal_file_type,
                        gl_type: (formData.gl_type as TApiGlType) || undefined,
                        import_available: formData.import_available,
                        final_file_destination: formData.final_destination,
                        monthly_deadline_day: deadlineNum,
                        start_period: startPeriod,
                        // accepts_otb always true per spec; backend enforces
                        accepts_otb: true,
                        column_mappings:
                            columnMappingsToCreate.length > 0 ? columnMappingsToCreate : undefined,
                        propcode_mappings:
                            propcodeMappingsToCreate.length > 0
                                ? propcodeMappingsToCreate
                                : undefined,
                        tb_filtering_rules:
                            tbFilteringRules.length > 0
                                ? tbFilteringRules.map((r) => ({
                                      filtering_column_header: r.filteringColumnHeader,
                                      filtering_value: r.filteringValue,
                                      include_filtered_values: r.includeFilteredValues,
                                  }))
                                : undefined,
                        // GL rules are only meaningful on a TB+GL deal; sending them for a
                        // TB-only deal leaves dormant rules that would activate unannounced
                        // if the deal is later switched to TB+GL.
                        gl_filtering_rules:
                            isTbAndGl && glFilteringRules.length > 0
                                ? glFilteringRules.map((r) => ({
                                      filtering_column_header: r.filteringColumnHeader,
                                      filtering_value: r.filteringValue,
                                      include_filtered_values: r.includeFilteredValues,
                                  }))
                                : undefined,
                        // Copy deal: settings carried over from the source deal.
                        ...(extraFields && {
                            quarterly_deadline_day: extraFields.quarterly_deadline_day,
                            year_end_deadline: extraFields.year_end_deadline || null,
                            yardi_entity_id: extraFields.yardi_entity_id.trim() || null,
                            yardi_database: extraFields.yardi_database.trim() || null,
                            otb_comparison_enabled: extraFields.otb_comparison_enabled,
                            accepts_fin_journals: extraFields.accepts_fin_journals,
                            is_test: extraFields.is_test,
                            notes: extraFields.notes.trim() || null,
                            import_source_book_names: extraFields.import_source_book_names,
                        }),
                    }));
                setCreatedDeal(deal);

                // Upload OTB file or generate placeholder for the created deal
                if (deal?.id) {
                    try {
                        let otbResult: TApiOTBUploadResponse;
                        if (usePlaceholderOTB) {
                            // Seed the placeholder lines from the primary identifier's
                            // source code when the file maps one.
                            const firstPropcode = (
                                propcodeMappings.rows.find(
                                    (r) => r.kind === DEFAULT_PROPCODE_KIND,
                                ) ?? propcodeMappings.rows[0]!
                            ).source_propcode;
                            otbResult = await onGeneratePlaceholderOTB(deal.id, firstPropcode);
                        } else if (otbUpload.file) {
                            otbResult = await onUploadOTB(deal.id, otbUpload.file);
                        } else {
                            // Should not reach here due to validation above
                            setIsSubmitting(false);
                            return;
                        }

                        if (!otbResult.success) {
                            setError(
                                `Deal created successfully, but OTB ${usePlaceholderOTB ? "generation" : "upload"} failed: ${
                                    otbResult.errors.join("; ") || "Unknown error"
                                }. Fix the OTB and click "Retry OTB" — the deal is not created again.`,
                            );
                            setIsSubmitting(false);
                            return;
                        }
                    } catch (otbErr) {
                        setError(
                            `Deal created successfully, but OTB ${usePlaceholderOTB ? "generation" : "upload"} failed: ${
                                otbErr instanceof Error ? otbErr.message : "Unknown error"
                            }. Fix the OTB and click "Retry OTB" — the deal is not created again.`,
                        );
                        setIsSubmitting(false);
                        return;
                    }
                }

                setIsSubmitting(false);
                if (prefill && deal) {
                    onDealCreateSuccess?.(deal, bpId ?? pcId);
                } else {
                    onDealCreateSuccess?.();
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to create deal");
                setIsSubmitting(false);
            }
        };

        const isLoading = serviceProviders.loading || portfolioCompanies.loading || users.loading;

        // Get step description
        const getStepDescription = () => {
            switch (currentStep) {
                case 1:
                    return "Step 1: Deal Information";
                case 2:
                    return "Step 2: Column Mapping";
                case 3:
                    return "Step 3: Propcode Mapping";
                case 4:
                    return "Step 4: OTB Upload";
                default:
                    return "";
            }
        };

        return (
            <PageLayout
                title={prefill ? "Copy Deal" : "Create Deal"}
                description={
                    prefill
                        ? `Copying ${prefill.sourceDealName} · ${getStepDescription()}`
                        : getStepDescription()
                }
            >
                <Card>
                    <CardContent className="pt-6">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <Loader size="lg" />
                            </div>
                        ) : currentStep === 1 ? (
                            /* Step 1: Deal Required Info */
                            <div className="space-y-6">
                                {error && (
                                    <div className="rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Name <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        name: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                placeholder="Q1 2026 Trial Balance"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Relationships */}
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4">
                                        {/* Finance Team */}
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Finance Team{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <MultiSelect
                                                options={
                                                    users.data?.map((u) => ({
                                                        id: u.id,
                                                        label: `${u.display_name} (${u.email})`,
                                                    })) || []
                                                }
                                                selectedIds={formData.finance_user_ids}
                                                onChange={(ids) =>
                                                    setFormData({
                                                        ...formData,
                                                        finance_user_ids: ids,
                                                    })
                                                }
                                                placeholder="Select finance team members..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Configuration */}
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                File Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.deal_file_type}
                                                onChange={(e) => {
                                                    const newFileType = e.target
                                                        .value as TApiDealFileType;
                                                    setFormData({
                                                        ...formData,
                                                        deal_file_type: newFileType,
                                                        gl_type:
                                                            newFileType !== "tb_and_gl"
                                                                ? ""
                                                                : formData.gl_type,
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {dealFileTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {formData.deal_file_type === "tb_and_gl" && (
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    GL Type{" "}
                                                    <span className="text-destructive">*</span>
                                                </label>
                                                <select
                                                    value={formData.gl_type}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            gl_type: e.target.value,
                                                        })
                                                    }
                                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="">Select GL type...</option>
                                                    <option value="ytd">YTD</option>
                                                    <option value="mtd">MTD</option>
                                                </select>
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Final Destination{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.final_destination}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        final_destination: e.target
                                                            .value as TApiFinalFileDestination,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {finalDestinations.map((dest) => (
                                                    <option key={dest.value} value={dest.value}>
                                                        {dest.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Deadline Day{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.deadline}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "") {
                                                        setFormData({
                                                            ...formData,
                                                            deadline: "",
                                                        });
                                                        setDeadlineError(null);
                                                        return;
                                                    }
                                                    const num = parseInt(val);
                                                    setFormData({
                                                        ...formData,
                                                        deadline: num,
                                                    });
                                                    if (num < 1 || num > 28) {
                                                        setDeadlineError(
                                                            "Must be between 1 and 28",
                                                        );
                                                    } else {
                                                        setDeadlineError(null);
                                                    }
                                                }}
                                                className={`w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary ${deadlineError ? "border-destructive" : ""}`}
                                                min={1}
                                                max={28}
                                            />
                                            {deadlineError ? (
                                                <p className="text-xs text-destructive mt-1">
                                                    {deadlineError}
                                                </p>
                                            ) : deadlineWarning ? (
                                                <p
                                                    className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                                                    data-testid="deadline-day-warning"
                                                >
                                                    {deadlineWarning.message}
                                                </p>
                                            ) : (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Day of month (1-28)
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Start Period Month{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.start_period_month}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        start_period_month: parseInt(
                                                            e.target.value,
                                                        ),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                <option value={1}>January</option>
                                                <option value={2}>February</option>
                                                <option value={3}>March</option>
                                                <option value={4}>April</option>
                                                <option value={5}>May</option>
                                                <option value={6}>June</option>
                                                <option value={7}>July</option>
                                                <option value={8}>August</option>
                                                <option value={9}>September</option>
                                                <option value={10}>October</option>
                                                <option value={11}>November</option>
                                                <option value={12}>December</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                Start Period Year{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.start_period_year}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        start_period_year: parseInt(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {Array.from({ length: 10 }, (_, i) => {
                                                    const year = getNow().getFullYear() - 2 + i;
                                                    return (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Copy deal: settings carried over that have no other step */}
                                {extraFields && prefill && (
                                    <details
                                        className="border rounded-lg p-4"
                                        data-testid="copy-deal-additional-settings"
                                    >
                                        <summary className="cursor-pointer text-sm font-medium">
                                            Additional settings (copied from{" "}
                                            {prefill.sourceDealName})
                                        </summary>
                                        <div className="mt-4 space-y-4">
                                            {extraFields.yardi_entity_id.trim() &&
                                                extraFields.yardi_entity_id.trim() ===
                                                    prefill.sourceYardiEntityId.trim() &&
                                                extraFields.yardi_database.trim() ===
                                                    prefill.sourceYardiDatabase.trim() && (
                                                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                                                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                                        <span>
                                                            Yardi Entity ID and Database are the
                                                            same as on {prefill.sourceDealName}.
                                                            Both deals would export to the same
                                                            Yardi entity.
                                                        </span>
                                                    </div>
                                                )}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Yardi Entity ID
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={100}
                                                        value={extraFields.yardi_entity_id}
                                                        onChange={(e) =>
                                                            setExtraFields({
                                                                ...extraFields,
                                                                yardi_entity_id: e.target.value,
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Yardi Database
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={100}
                                                        value={extraFields.yardi_database}
                                                        onChange={(e) =>
                                                            setExtraFields({
                                                                ...extraFields,
                                                                yardi_database: e.target.value,
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Quarterly Deadline Day
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={28}
                                                        value={
                                                            extraFields.quarterly_deadline_day ?? ""
                                                        }
                                                        onChange={(e) => {
                                                            const num = parseInt(
                                                                e.target.value,
                                                                10,
                                                            );
                                                            setExtraFields({
                                                                ...extraFields,
                                                                quarterly_deadline_day:
                                                                    Number.isNaN(num)
                                                                        ? null
                                                                        : Math.min(
                                                                              28,
                                                                              Math.max(1, num),
                                                                          ),
                                                            });
                                                        }}
                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Year-End Deadline
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={extraFields.year_end_deadline ?? ""}
                                                        onChange={(e) =>
                                                            setExtraFields({
                                                                ...extraFields,
                                                                year_end_deadline:
                                                                    e.target.value || null,
                                                            })
                                                        }
                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-6 text-sm">
                                                {(
                                                    [
                                                        [
                                                            "otb_comparison_enabled",
                                                            "OTB comparison",
                                                        ],
                                                        [
                                                            "accepts_fin_journals",
                                                            "Accepts Financial Journals",
                                                        ],
                                                        ["is_test", "Test deal"],
                                                    ] as const
                                                ).map(([key, label]) => (
                                                    <label
                                                        key={key}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={extraFields[key]}
                                                            onChange={(e) =>
                                                                setExtraFields({
                                                                    ...extraFields,
                                                                    [key]: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                        {label}
                                                    </label>
                                                ))}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Import Source Books
                                                </label>
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {extraFields.import_source_book_names.map(
                                                        (name) => (
                                                            <span
                                                                key={name}
                                                                className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs"
                                                            >
                                                                {name}
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Remove ${name}`}
                                                                    onClick={() =>
                                                                        setExtraFields({
                                                                            ...extraFields,
                                                                            import_source_book_names:
                                                                                extraFields.import_source_book_names.filter(
                                                                                    (n) =>
                                                                                        n !== name,
                                                                                ),
                                                                        })
                                                                    }
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </span>
                                                        ),
                                                    )}
                                                </div>
                                                <input
                                                    type="text"
                                                    maxLength={255}
                                                    value={newImportBookName}
                                                    onChange={(e) =>
                                                        setNewImportBookName(e.target.value)
                                                    }
                                                    onKeyDown={(e) => {
                                                        const name = newImportBookName.trim();
                                                        if (e.key !== "Enter" || !name) return;
                                                        e.preventDefault();
                                                        if (
                                                            !extraFields.import_source_book_names.includes(
                                                                name,
                                                            )
                                                        ) {
                                                            setExtraFields({
                                                                ...extraFields,
                                                                import_source_book_names: [
                                                                    ...extraFields.import_source_book_names,
                                                                    name,
                                                                ],
                                                            });
                                                        }
                                                        setNewImportBookName("");
                                                    }}
                                                    placeholder="Type a book name and press Enter"
                                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Notes
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={extraFields.notes}
                                                    onChange={(e) =>
                                                        setExtraFields({
                                                            ...extraFields,
                                                            notes: e.target.value,
                                                        })
                                                    }
                                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                />
                                            </div>
                                        </div>
                                    </details>
                                )}

                                {/* Step 1 Actions */}
                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => onCancel(bpId)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => void handleNextFromStep1()}
                                        disabled={!canProceedFromStep1 || isCheckingName}
                                    >
                                        Next
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ) : currentStep === 2 ? (
                            /* Step 2: Column Mapping */
                            <div className="space-y-6">
                                {error && (
                                    <div className="rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-sm mb-6">
                                    <span className="text-muted-foreground">
                                        Creating deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    <span className="text-muted-foreground">
                                        File Type:{" "}
                                        <strong>
                                            {formData.deal_file_type === "tb_and_gl"
                                                ? "TB and GL"
                                                : "TB Only"}
                                        </strong>
                                    </span>
                                </div>

                                {prefill && (
                                    <div
                                        className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-400 flex items-start gap-3"
                                        data-testid="copy-deal-columns-banner"
                                    >
                                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                                        <span>
                                            Columns, mappings and filtering rules were copied from{" "}
                                            <strong>{prefill.sourceDealName}</strong>. Change any of
                                            them, or select a new sample file to replace the copied
                                            columns.
                                        </span>
                                    </div>
                                )}

                                {/* TB Column Mapping Section */}
                                <div className="border rounded-lg p-6">
                                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                                        <FileSpreadsheet className="h-5 w-5" />
                                        TB Column Mapping
                                    </h3>

                                    {/* File Upload for TB */}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Header Row Number
                                                </label>
                                                <input
                                                    type="number"
                                                    value={tbColumnMapping.headerRowNumber}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === "") {
                                                            setTbColumnMapping((prev) => ({
                                                                ...prev,
                                                                headerRowNumber: "",
                                                            }));
                                                            return;
                                                        }
                                                        const num = parseInt(val, 10);
                                                        if (Number.isNaN(num)) return;
                                                        setTbColumnMapping((prev) => ({
                                                            ...prev,
                                                            headerRowNumber: Math.max(1, num),
                                                        }));
                                                    }}
                                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    min={1}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Select file for TB Columns
                                                </label>
                                                <div className="flex gap-2">
                                                    <label className="flex-1">
                                                        <input
                                                            type="file"
                                                            accept=".xlsx,.csv"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    handleColumnFileSelect(
                                                                        "tb",
                                                                        file,
                                                                        resolveHeaderRowNumberForApi(
                                                                            tbColumnMapping.headerRowNumber,
                                                                        ),
                                                                    );
                                                                }
                                                                e.target.value = "";
                                                            }}
                                                            className="hidden"
                                                            disabled={tbColumnMapping.isLoading}
                                                        />
                                                        <div
                                                            className={cn(
                                                                "px-4 py-2 border rounded-lg text-center cursor-pointer transition-colors",
                                                                tbColumnMapping.isLoading
                                                                    ? "opacity-50 cursor-not-allowed"
                                                                    : tbColumnMapping.file ||
                                                                        tbColumnMapping.origin ===
                                                                            "copied"
                                                                      ? "border-primary bg-primary/5"
                                                                      : "hover:border-primary/50 hover:bg-primary/5",
                                                            )}
                                                        >
                                                            {tbColumnMapping.isLoading ? (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Loader size="sm" />
                                                                    Extracting columns...
                                                                </span>
                                                            ) : tbColumnMapping.file ? (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Check className="h-4 w-4 text-green-500" />
                                                                    {tbColumnMapping.file.name}
                                                                </span>
                                                            ) : tbColumnMapping.origin ===
                                                              "copied" ? (
                                                                <span
                                                                    className="flex items-center justify-center gap-2"
                                                                    data-testid="copied-tb-columns"
                                                                >
                                                                    <Check className="h-4 w-4 text-green-500" />
                                                                    Columns copied · select a new
                                                                    file to replace
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Upload className="h-4 w-4" />
                                                                    Select file for TB Columns
                                                                </span>
                                                            )}
                                                        </div>
                                                    </label>
                                                    {tbColumnMapping.file && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleClearColumnFile("tb")
                                                            }
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {tbColumnMapping.error && (
                                            <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                                <span>{tbColumnMapping.error}</span>
                                            </div>
                                        )}

                                        {tbColumnMapping.warnings.length > 0 && (
                                            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                                <div>
                                                    <p className="font-medium">Warnings:</p>
                                                    <ul className="list-disc list-inside text-xs mt-1">
                                                        {tbColumnMapping.warnings
                                                            .slice(0, 3)
                                                            .map((w, i) => (
                                                                <li key={i}>{w}</li>
                                                            ))}
                                                        {tbColumnMapping.warnings.length > 3 && (
                                                            <li>
                                                                ...and{" "}
                                                                {tbColumnMapping.warnings.length -
                                                                    3}{" "}
                                                                more
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {/* TB Column Mapping Fields */}
                                        {tbColumnMapping.columns.length > 0 && (
                                            <div className="border rounded-lg p-4 bg-muted/20">
                                                <h4 className="text-sm font-medium mb-3">
                                                    TB Column Mappings
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {TB_COLUMN_FIELDS.map((field) => {
                                                        const isAmount =
                                                            field.fieldType === "amount";
                                                        const currentValue =
                                                            tbColumnMapping.mappings[
                                                                field.fieldType
                                                            ] || "";
                                                        const isFormulaActive =
                                                            isAmount &&
                                                            !!parseFormulaExpression(currentValue);
                                                        const isFieldRequired =
                                                            getRequiredColumnFields("tb").some(
                                                                (f) =>
                                                                    f.fieldType === field.fieldType,
                                                            );
                                                        return (
                                                            <div key={field.fieldType}>
                                                                <label className="block text-sm font-medium mb-1">
                                                                    {field.label}{" "}
                                                                    {isFieldRequired && (
                                                                        <span className="text-destructive">
                                                                            *
                                                                        </span>
                                                                    )}
                                                                </label>
                                                                {isAmount ? (
                                                                    <div className="flex items-center gap-2">
                                                                        {isFormulaActive ? (
                                                                            <div className="flex-1 px-3 py-2 text-sm border rounded-lg bg-muted text-muted-foreground font-mono">
                                                                                {(() => {
                                                                                    const f =
                                                                                        parseFormulaExpression(
                                                                                            currentValue,
                                                                                        );
                                                                                    return f
                                                                                        ? `${f.left} ${f.op} ${f.right}`
                                                                                        : currentValue;
                                                                                })()}
                                                                            </div>
                                                                        ) : (
                                                                            <select
                                                                                value={currentValue}
                                                                                onChange={(e) =>
                                                                                    handleColumnMappingChange(
                                                                                        "tb",
                                                                                        field.fieldType,
                                                                                        e.target
                                                                                            .value,
                                                                                    )
                                                                                }
                                                                                className="flex-1 px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                                            >
                                                                                <option value="">
                                                                                    Select column...
                                                                                </option>
                                                                                {tbColumnMapping.columns.map(
                                                                                    (col) => (
                                                                                        <option
                                                                                            key={
                                                                                                col
                                                                                            }
                                                                                            value={
                                                                                                col
                                                                                            }
                                                                                        >
                                                                                            {col}
                                                                                        </option>
                                                                                    ),
                                                                                )}
                                                                            </select>
                                                                        )}
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                setTbFormulaOpen(
                                                                                    true,
                                                                                )
                                                                            }
                                                                            className="whitespace-nowrap text-xs shrink-0"
                                                                        >
                                                                            {isFormulaActive
                                                                                ? "Edit formula"
                                                                                : "Use formula"}
                                                                        </Button>
                                                                        {isFormulaActive && (
                                                                            <Button
                                                                                type="button"
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() =>
                                                                                    handleColumnMappingChange(
                                                                                        "tb",
                                                                                        "amount",
                                                                                        "",
                                                                                    )
                                                                                }
                                                                                className="text-xs px-2 shrink-0"
                                                                                title="Clear formula"
                                                                            >
                                                                                <X className="h-3 w-3" />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <select
                                                                        value={currentValue}
                                                                        onChange={(e) =>
                                                                            handleColumnMappingChange(
                                                                                "tb",
                                                                                field.fieldType,
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                                    >
                                                                        <option value="">
                                                                            Select column...
                                                                        </option>
                                                                        {tbColumnMapping.columns.map(
                                                                            (col) => (
                                                                                <option
                                                                                    key={col}
                                                                                    value={col}
                                                                                >
                                                                                    {col}
                                                                                </option>
                                                                            ),
                                                                        )}
                                                                    </select>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {(() => {
                                                    const f = parseFormulaExpression(
                                                        tbColumnMapping.mappings["amount"] ?? "",
                                                    );
                                                    return (
                                                        <FormulaModal
                                                            open={tbFormulaOpen}
                                                            onClose={() => setTbFormulaOpen(false)}
                                                            columns={tbColumnMapping.columns}
                                                            initialLeft={f?.left ?? ""}
                                                            initialOp={f?.op ?? "-"}
                                                            initialRight={f?.right ?? ""}
                                                            onSubmit={(left, op, right) =>
                                                                handleColumnMappingChange(
                                                                    "tb",
                                                                    "amount",
                                                                    buildFormulaExpression(
                                                                        left,
                                                                        op,
                                                                        right,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* TB Filtering Rules */}
                                {tbColumnMapping.columns.length > 0 && (
                                    <div className="border rounded-lg p-6">
                                        <OrganismFilteringRules
                                            fileType="tb"
                                            availableColumns={tbColumnMapping.columns}
                                            rules={tbFilteringRules}
                                            onChange={setTbFilteringRules}
                                        />
                                    </div>
                                )}

                                {/* GL Column Mapping Section (only for TB and GL) */}
                                {isTbAndGl && (
                                    <div className="border rounded-lg p-6">
                                        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                                            <FileSpreadsheet className="h-5 w-5" />
                                            GL Column Mapping
                                        </h3>

                                        {/* File Upload for GL */}
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Header Row Number
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={glColumnMapping.headerRowNumber}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val === "") {
                                                                setGlColumnMapping((prev) => ({
                                                                    ...prev,
                                                                    headerRowNumber: "",
                                                                }));
                                                                return;
                                                            }
                                                            const num = parseInt(val, 10);
                                                            if (Number.isNaN(num)) return;
                                                            setGlColumnMapping((prev) => ({
                                                                ...prev,
                                                                headerRowNumber: Math.max(1, num),
                                                            }));
                                                        }}
                                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                        min={1}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1">
                                                        Select file for GL Columns
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <label className="flex-1">
                                                            <input
                                                                type="file"
                                                                accept=".xlsx,.csv"
                                                                onChange={(e) => {
                                                                    const file =
                                                                        e.target.files?.[0];
                                                                    if (file) {
                                                                        handleColumnFileSelect(
                                                                            "gl",
                                                                            file,
                                                                            resolveHeaderRowNumberForApi(
                                                                                glColumnMapping.headerRowNumber,
                                                                            ),
                                                                        );
                                                                    }
                                                                    e.target.value = "";
                                                                }}
                                                                className="hidden"
                                                                disabled={glColumnMapping.isLoading}
                                                            />
                                                            <div
                                                                className={cn(
                                                                    "px-4 py-2 border rounded-lg text-center cursor-pointer transition-colors",
                                                                    glColumnMapping.isLoading
                                                                        ? "opacity-50 cursor-not-allowed"
                                                                        : glColumnMapping.file ||
                                                                            glColumnMapping.origin ===
                                                                                "copied"
                                                                          ? "border-primary bg-primary/5"
                                                                          : "hover:border-primary/50 hover:bg-primary/5",
                                                                )}
                                                            >
                                                                {glColumnMapping.isLoading ? (
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        <Loader size="sm" />
                                                                        Extracting columns...
                                                                    </span>
                                                                ) : glColumnMapping.file ? (
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        <Check className="h-4 w-4 text-green-500" />
                                                                        {glColumnMapping.file.name}
                                                                    </span>
                                                                ) : glColumnMapping.origin ===
                                                                  "copied" ? (
                                                                    <span
                                                                        className="flex items-center justify-center gap-2"
                                                                        data-testid="copied-gl-columns"
                                                                    >
                                                                        <Check className="h-4 w-4 text-green-500" />
                                                                        Columns copied · select a
                                                                        new file to replace
                                                                    </span>
                                                                ) : (
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        <Upload className="h-4 w-4" />
                                                                        Select file for GL Columns
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </label>
                                                        {glColumnMapping.file && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleClearColumnFile("gl")
                                                                }
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {glColumnMapping.error && (
                                                <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                                    <span>{glColumnMapping.error}</span>
                                                </div>
                                            )}

                                            {glColumnMapping.warnings.length > 0 && (
                                                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                                    <div>
                                                        <p className="font-medium">Warnings:</p>
                                                        <ul className="list-disc list-inside text-xs mt-1">
                                                            {glColumnMapping.warnings
                                                                .slice(0, 3)
                                                                .map((w, i) => (
                                                                    <li key={i}>{w}</li>
                                                                ))}
                                                            {glColumnMapping.warnings.length >
                                                                3 && (
                                                                <li>
                                                                    ...and{" "}
                                                                    {glColumnMapping.warnings
                                                                        .length - 3}{" "}
                                                                    more
                                                                </li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}

                                            {/* GL Column Mapping Fields */}
                                            {glColumnMapping.columns.length > 0 && (
                                                <div className="border rounded-lg p-4 bg-muted/20">
                                                    <h4 className="text-sm font-medium mb-3">
                                                        GL Column Mappings
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {GL_COLUMN_FIELDS.map((field) => {
                                                            const isAmount =
                                                                field.fieldType === "amount";
                                                            const currentValue =
                                                                glColumnMapping.mappings[
                                                                    field.fieldType
                                                                ] || "";
                                                            const isFormulaActive =
                                                                isAmount &&
                                                                !!parseFormulaExpression(
                                                                    currentValue,
                                                                );
                                                            const isFieldRequired =
                                                                getRequiredColumnFields("gl").some(
                                                                    (f) =>
                                                                        f.fieldType ===
                                                                        field.fieldType,
                                                                );
                                                            return (
                                                                <div key={field.fieldType}>
                                                                    <label className="block text-sm font-medium mb-1">
                                                                        {field.label}{" "}
                                                                        {isFieldRequired && (
                                                                            <span className="text-destructive">
                                                                                *
                                                                            </span>
                                                                        )}
                                                                    </label>
                                                                    {isAmount ? (
                                                                        <div className="flex items-center gap-2">
                                                                            {isFormulaActive ? (
                                                                                <div className="flex-1 px-3 py-2 text-sm border rounded-lg bg-muted text-muted-foreground font-mono">
                                                                                    {(() => {
                                                                                        const f =
                                                                                            parseFormulaExpression(
                                                                                                currentValue,
                                                                                            );
                                                                                        return f
                                                                                            ? `${f.left} ${f.op} ${f.right}`
                                                                                            : currentValue;
                                                                                    })()}
                                                                                </div>
                                                                            ) : (
                                                                                <select
                                                                                    value={
                                                                                        currentValue
                                                                                    }
                                                                                    onChange={(e) =>
                                                                                        handleColumnMappingChange(
                                                                                            "gl",
                                                                                            field.fieldType,
                                                                                            e.target
                                                                                                .value,
                                                                                        )
                                                                                    }
                                                                                    className="flex-1 px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                                                >
                                                                                    <option value="">
                                                                                        Select
                                                                                        column...
                                                                                    </option>
                                                                                    {glColumnMapping.columns.map(
                                                                                        (col) => (
                                                                                            <option
                                                                                                key={
                                                                                                    col
                                                                                                }
                                                                                                value={
                                                                                                    col
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    col
                                                                                                }
                                                                                            </option>
                                                                                        ),
                                                                                    )}
                                                                                </select>
                                                                            )}
                                                                            <Button
                                                                                type="button"
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() =>
                                                                                    setGlFormulaOpen(
                                                                                        true,
                                                                                    )
                                                                                }
                                                                                className="whitespace-nowrap text-xs shrink-0"
                                                                            >
                                                                                {isFormulaActive
                                                                                    ? "Edit formula"
                                                                                    : "Use formula"}
                                                                            </Button>
                                                                            {isFormulaActive && (
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="outline"
                                                                                    size="sm"
                                                                                    onClick={() =>
                                                                                        handleColumnMappingChange(
                                                                                            "gl",
                                                                                            "amount",
                                                                                            "",
                                                                                        )
                                                                                    }
                                                                                    className="text-xs px-2 shrink-0"
                                                                                    title="Clear formula"
                                                                                >
                                                                                    <X className="h-3 w-3" />
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <select
                                                                            value={currentValue}
                                                                            onChange={(e) =>
                                                                                handleColumnMappingChange(
                                                                                    "gl",
                                                                                    field.fieldType,
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                                        >
                                                                            <option value="">
                                                                                Select column...
                                                                            </option>
                                                                            {glColumnMapping.columns.map(
                                                                                (col) => (
                                                                                    <option
                                                                                        key={col}
                                                                                        value={col}
                                                                                    >
                                                                                        {col}
                                                                                    </option>
                                                                                ),
                                                                            )}
                                                                        </select>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {(() => {
                                                        const f = parseFormulaExpression(
                                                            glColumnMapping.mappings["amount"] ??
                                                                "",
                                                        );
                                                        return (
                                                            <FormulaModal
                                                                open={glFormulaOpen}
                                                                onClose={() =>
                                                                    setGlFormulaOpen(false)
                                                                }
                                                                columns={glColumnMapping.columns}
                                                                initialLeft={f?.left ?? ""}
                                                                initialOp={f?.op ?? "-"}
                                                                initialRight={f?.right ?? ""}
                                                                onSubmit={(left, op, right) =>
                                                                    handleColumnMappingChange(
                                                                        "gl",
                                                                        "amount",
                                                                        buildFormulaExpression(
                                                                            left,
                                                                            op,
                                                                            right,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* GL Filtering Rules */}
                                {isTbAndGl && glColumnMapping.columns.length > 0 && (
                                    <div className="border rounded-lg p-6">
                                        <OrganismFilteringRules
                                            fileType="gl"
                                            availableColumns={glColumnMapping.columns}
                                            rules={glFilteringRules}
                                            onChange={setGlFilteringRules}
                                        />
                                    </div>
                                )}

                                {/* Step 2 Actions */}
                                <div className="flex justify-between gap-3 pt-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleBackFromStep2}
                                        disabled={isSubmitting}
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => onCancel(bpId)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleNextFromStep2}
                                            disabled={!canProceedFromColumnMapping}
                                        >
                                            Next
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : currentStep === 3 ? (
                            /* Step 3: Propcode Mapping */
                            <div className="space-y-6">
                                {error && (
                                    <div className="rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-sm mb-6">
                                    <span className="text-muted-foreground">
                                        Creating deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    <span className="text-muted-foreground flex items-center gap-2">
                                        <MapPin className="h-4 w-4" />
                                        Propcode Mapping <span className="text-destructive">*</span>
                                    </span>
                                </div>

                                {/* Description */}
                                <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground">
                                    <p>
                                        <strong className="text-foreground">Required:</strong>{" "}
                                        Upload an Excel file to map Service Provider property codes
                                        to ERP/BRE property codes.
                                    </p>
                                    <p className="mt-2">
                                        <strong>{PROPCODE_UPLOAD_HINT}</strong>
                                    </p>
                                </div>

                                {/* File Upload Area */}
                                {prefill && (
                                    <div
                                        className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-400 flex items-start gap-3"
                                        data-testid="copy-deal-propcodes-banner"
                                    >
                                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                                        <span>
                                            Propcode mappings were copied from{" "}
                                            <strong>{prefill.sourceDealName}</strong> and validated
                                            again. Download the file to edit it, then upload it
                                            back, or upload a different file.
                                        </span>
                                    </div>
                                )}

                                <PropcodeMappingUploadSection
                                    mappingData={propcodeMappings}
                                    onFileSelect={handlePropcodeFileSelect}
                                    onClearFile={handleClearPropcodeFile}
                                    onDownloadFile={
                                        prefill && propcodeMappings.file
                                            ? () => downloadFile(propcodeMappings.file!)
                                            : undefined
                                    }
                                />

                                {/* Step 3 Actions */}
                                <div className="flex justify-between gap-3 pt-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleBackFromStep3}
                                        disabled={isSubmitting}
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => onCancel(bpId)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleNextFromStep3}
                                            disabled={
                                                propcodeMappings.error !== null ||
                                                !propcodeMappings.file ||
                                                propcodeMappings.rows.length === 0 ||
                                                propcodeMappings.isLoading
                                            }
                                        >
                                            Next
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : currentStep === 4 ? (
                            /* Step 4: OTB Upload */
                            <div className="space-y-6">
                                {error && (
                                    <div className="rounded-lg bg-destructive/10 p-4 text-destructive text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-sm mb-6">
                                    <span className="text-muted-foreground">
                                        Deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    <span className="text-muted-foreground flex items-center gap-2">
                                        <Upload className="h-4 w-4" />
                                        Opening Trial Balance{" "}
                                        <span className="text-destructive">*</span>
                                    </span>
                                </div>

                                {prefill && !usePlaceholderOTB && (
                                    <div
                                        className={cn(
                                            "rounded-lg p-4 text-sm flex items-start gap-3",
                                            isCopiedOtbAttached
                                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                                                : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400",
                                        )}
                                        data-testid="copy-deal-otb-banner"
                                    >
                                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                                        <div className="space-y-2">
                                            {!prefill.otbFile ? (
                                                <p>
                                                    {prefill.otbUnavailableReason ??
                                                        `${prefill.sourceDealName} has no OTB to copy. Upload an OTB file or use a placeholder.`}
                                                </p>
                                            ) : isCopiedOtbAttached ? (
                                                <p>
                                                    The OTB of {prefill.sourceDealName} is attached,
                                                    so this deal starts from its opening balances as
                                                    they are. It was checked against this
                                                    deal&apos;s TB column mappings and filtering
                                                    rules only, not against the period. Replace it
                                                    if this deal does not start from the same
                                                    balances.
                                                </p>
                                            ) : otbUpload.file ? null : (
                                                <>
                                                    <p>
                                                        {prefill.sourceStartPeriod
                                                            ? `${prefill.sourceDealName} starts in ${prefill.sourceStartPeriod}, this deal in ${currentStartPeriod}. Its OTB holds the opening balances for its own start, so it was not attached.`
                                                            : `${prefill.sourceDealName} has no start period, so its OTB was not attached automatically.`}{" "}
                                                        Upload this deal&apos;s OTB, use a
                                                        placeholder, or use the copied OTB anyway.
                                                    </p>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleUseCopiedOtb}
                                                        data-testid="use-copied-otb"
                                                    >
                                                        Use the OTB of {prefill.sourceDealName}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Info message for OTB upload */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-400 flex items-start gap-3">
                                    <Upload className="h-5 w-5 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="font-medium">
                                            Upload Opening Trial Balance{" "}
                                            <span className="text-destructive">*</span>
                                        </p>
                                        <p className="mt-1">
                                            Please upload an Opening Trial Balance (OTB) file for
                                            the period{" "}
                                            <strong>{calculateOTBPeriod().substring(0, 7)}</strong>.
                                        </p>
                                    </div>
                                </div>

                                {/* OTB Upload Description */}
                                <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground">
                                    <p>
                                        <strong className="text-foreground">OTB Period:</strong>{" "}
                                        {calculateOTBPeriod().substring(0, 7)} (one month before
                                        start period)
                                    </p>
                                    <p className="mt-2">
                                        The OTB file represents opening balances.{" "}
                                        <strong>Balance must equal 0</strong> (debits = credits).
                                    </p>
                                </div>

                                {/* OTB File Upload Area */}
                                <div
                                    className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
                                        isDraggingOTB ? "border-primary bg-primary/5" : ""
                                    }`}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!usePlaceholderOTB) setIsDraggingOTB(true);
                                    }}
                                    onDragEnter={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!usePlaceholderOTB) setIsDraggingOTB(true);
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsDraggingOTB(false);
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsDraggingOTB(false);
                                        if (usePlaceholderOTB) return;
                                        const file = e.dataTransfer.files?.[0];
                                        if (file) handleOTBFileSelect(file);
                                    }}
                                >
                                    {usePlaceholderOTB && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <Check className="h-5 w-5 text-blue-500" />
                                                    <div>
                                                        <p className="font-medium text-blue-700 dark:text-blue-400">
                                                            Placeholder OTB will be generated
                                                        </p>
                                                        <p className="text-sm text-blue-600 dark:text-blue-500">
                                                            A minimal OTB with balancing entries
                                                            will be created automatically when the
                                                            deal is created.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex justify-center">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setUsePlaceholderOTB(false)}
                                                >
                                                    <Upload className="mr-2 h-4 w-4" />
                                                    Upload OTB instead
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {!usePlaceholderOTB &&
                                        otbUpload.status === "idle" &&
                                        !otbUpload.file && (
                                            <div className="flex flex-col items-center justify-center text-center">
                                                <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                                                <p className="text-lg font-medium mb-2">
                                                    Upload OTB File
                                                </p>
                                                <p className="text-sm text-muted-foreground mb-4">
                                                    Drag and drop an Excel file here, or click to
                                                    select
                                                </p>
                                                <input
                                                    type="file"
                                                    accept=".xlsx"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleOTBFileSelect(file);
                                                    }}
                                                    className="hidden"
                                                    id="otb-file-input"
                                                />
                                                <div className="flex gap-3">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={() =>
                                                            document
                                                                .getElementById("otb-file-input")
                                                                ?.click()
                                                        }
                                                    >
                                                        <FileSpreadsheet className="mr-2 h-4 w-4" />
                                                        Select File
                                                    </Button>
                                                    <Button
                                                        data-testid="otb-no-file-button"
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => setUsePlaceholderOTB(true)}
                                                        disabled={
                                                            propcodeMappings.rows.length === 0
                                                        }
                                                        title={
                                                            propcodeMappings.rows.length === 0
                                                                ? "Propcode mappings required for placeholder OTB"
                                                                : undefined
                                                        }
                                                    >
                                                        I don&apos;t have an OTB
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                    {otbUpload.status === "uploading" && (
                                        <div className="flex flex-col items-center justify-center text-center py-8">
                                            <Loader size="lg" className="mb-4" />
                                            <p className="text-lg font-medium">
                                                Uploading and validating...
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {otbUpload.file?.name}
                                            </p>
                                        </div>
                                    )}

                                    {otbUpload.status === "success" && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <Check className="h-5 w-5 text-green-500" />
                                                    <div>
                                                        <p className="font-medium text-green-700 dark:text-green-400">
                                                            OTB file selected
                                                        </p>
                                                        <p className="text-sm text-green-600 dark:text-green-500">
                                                            {otbUpload.file?.name}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleClearOTBFile}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {otbUpload.status === "error" && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 bg-destructive/10 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <AlertCircle className="h-5 w-5 text-destructive" />
                                                    <div>
                                                        <p className="font-medium text-destructive">
                                                            Upload failed
                                                        </p>
                                                        <p className="text-sm text-destructive/80">
                                                            {otbUpload.error}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleClearOTBFile}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            <div className="flex justify-center">
                                                <input
                                                    type="file"
                                                    accept=".xlsx"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleOTBFileSelect(file);
                                                    }}
                                                    className="hidden"
                                                    id="otb-file-retry-input"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() =>
                                                        document
                                                            .getElementById("otb-file-retry-input")
                                                            ?.click()
                                                    }
                                                >
                                                    <Upload className="mr-2 h-4 w-4" />
                                                    Try Again
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Step 4 Actions */}
                                <div className="flex justify-between gap-3 pt-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setCurrentStep(3)}
                                        disabled={isSubmitting || createdDeal !== null}
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => onCancel(bpId)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleCreateDeal}
                                            disabled={
                                                isSubmitting ||
                                                (!otbUpload.file && !usePlaceholderOTB) ||
                                                otbUpload.status === "error"
                                            }
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader size="sm" className="mr-2" />
                                                    {createdDeal ? "Retrying..." : "Creating..."}
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="mr-2 h-4 w-4" />
                                                    {createdDeal ? "Retry OTB" : "Create Deal"}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            </PageLayout>
        );
    };

    return ViewDealCreate;
};
